import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import {
  PhoneField,
  SelectField,
  Section,
  SubmitButton,
} from '@microrealestate/commonui/components';
import { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { StoreContext } from '../../store';
import useTranslation from 'next-translate/useTranslation';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useMediaQuery, Box, Typography, Grid, Radio, Button } from '@material-ui/core';
import { Home, Users, BarChart3, Bell } from 'lucide-react';

const sourceArray = [
  { id: "googleSearch", name: "Google Search" },
  { id: "instagram", name: "Instagram" },
  { id: "facebook", name: "Facebook" },
  { id: "referral", name: "Referral" },
  { id: "other", name: "Other" }
];

const experiencedOptions = [
  {
    id: 'yes',
    name: 'Pernah',
    description: 'Saya sudah pernah menggunakan aplikasi manajemen sebelumnya, misalnya untuk mengelola kos, apartemen, atau keuangan.'
  },
  {
    id: 'no',
    name: 'Tidak Pernah',
    description: 'Saya belum pernah menggunakan aplikasi manajemen. Saya mencatat secara manual atau melalui chat.'
  },
];

const validationSchemaStep1 = (t) => Yup.object().shape({
  whatsapp: Yup.string()
    .required(t("Whatsapp is a required field"))
    .test(
      'is-valid-whatsapp',
      t("Whatsapp must be at least 9 characters"),
      (value) => {
        if (!value) return false;
        const phoneNumber = parsePhoneNumberFromString(value);
        if (!phoneNumber) return false;
        const nationalNumber = phoneNumber.nationalNumber || '';
        return nationalNumber.length >= 9;
      }),
  });

const validationSchemaStep2 = (t) => Yup.object().shape({
  experiencedAppManagement: Yup.string().required(t("Please select experience")),
});

const validationSchemaTutorial = (t) => Yup.object().shape({
  // add validation rules if tutorial step requires inputs
});

const validationSchemaLeadSource = (t) => Yup.object().shape({
  leadSource: Yup.string().required(t("Lead source is a required field")),
});

const LandlordForm = observer(({ onSubmit, onSubmitted, onTrialStepChange }) => {
  const store = useContext(StoreContext);
  const { t } = useTranslation('common');
  const isMobile = useMediaQuery('(max-width:600px)');


  const availableSource = useMemo(() =>
    sourceArray.map(({ id, name }) => ({
      id,
      value: id,
      label: t(name)
    })), [t]);

  const initialValues = useMemo(() => {
    const selectedOrg = store.organization.selected;
    const selectedWhatsapp = selectedOrg?.whatsapp ? `+${selectedOrg.whatsapp}` : '';
    return {
      whatsapp: selectedWhatsapp || '',
      leadSource: selectedOrg?.leadSource || '',
      experiencedAppManagement: '',
    };
  }, [store.organization.selected]);

  const [step, setStep] = useState(0);

  const [experiencedAppManagementChoice, setExperiencedAppManagementChoice] = useState(initialValues.experiencedAppManagement);

  // Monitor when trial step becomes active to hide welcome text
  useEffect(() => {
    if (onTrialStepChange) {
      const isTrialStep = (experiencedAppManagementChoice === 'no' && step === 5) ||
                         (experiencedAppManagementChoice === 'yes' && step === 4);
      onTrialStepChange(isTrialStep);
    }
  }, [step, experiencedAppManagementChoice, onTrialStepChange]);

  const totalSteps = useMemo(() => {
    // total steps: whatsapp, benefits, experience, tutorial (if no experience), lead source, trial
    return experiencedAppManagementChoice === 'no' ? 6 : 5;
  }, [experiencedAppManagementChoice]);

  const getValidationSchema = () => {
    if (step === 0) return validationSchemaStep1(t);
    if (step === 1) return Yup.object(); // no validation for benefits step
    if (step === 2) return validationSchemaStep2(t); // experience step shifted to 2
    if (step === 3 && experiencedAppManagementChoice === 'no') return validationSchemaTutorial(t);
    if ((step === 3 && experiencedAppManagementChoice !== 'yes') || (step === 4 && experiencedAppManagementChoice === 'no')) {
      return validationSchemaLeadSource(t);
    }
    return Yup.object();
  };

  const _onSubmit = useCallback(async (values) => {
    if (step < totalSteps - 1) {
      if (step === 2) { // experience step now step 2
        setExperiencedAppManagementChoice(values.experiencedAppManagement);
      }
      setStep(step + 1);
    } else {
      const updatedSettings = {
        // TODO: comment out deprecated attribute
        // -- profile
        whatsapp: values.whatsapp,
        // -- metadata
        locale: 'id',
        currency: 'IDR',
        leadSource: values.leadSource,
        experiencedAppManagement: values.experiencedAppManagement === 'yes',
        // -- deprecated
        addresses: [],
        isCompany: false,
        taxIdNumber: null,
      };

      await onSubmit(updatedSettings);

      onSubmitted?.({
        isOrgNameChanged: false,
      });
    }
  }, [step, totalSteps, onSubmit, onSubmitted]);

  const validationSchema = getValidationSchema();

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={_onSubmit}
        enableReinitialize>
        {({ isSubmitting, values, setFieldValue }) => {
          if (values.experiencedAppManagement !== experiencedAppManagementChoice) {
            setExperiencedAppManagementChoice(values.experiencedAppManagement);
          }

          return (
            <Form autoComplete="off" style={{ paddingBottom: '80px' }}>
              <Box my={4}>
                {step === 0 && (
                  <>
                    <Box pb={2}>
                      <Typography variant='h6'>{t('Landlord information')}</Typography>
                    </Box>
                    <Box>
                      <PhoneField label={t('Whatsapp')} name="whatsapp" className='whatsapp-field' />
                    </Box>
                  </>
                )}

                {step === 1 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Kenapa pilih SuperKos?
                    </Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Dengan SuperKos, mengelola kos jadi lebih mudah, cepat, dan transparan.
                      Berikut manfaat yang bisa langsung anda rasakan:
                    </Typography>

                    <Grid container spacing={2} style={{ marginTop: 16 }}>
                      {[
                        { icon: "📊", title: "Pantau Keuangan", desc: "Semua pemasukan dan pengeluaran tercatat otomatis." },
                        { icon: "🏠", title: "Kelola Kamar", desc: "Status kamar kosong, terisi, atau jatuh tempo sewa bisa dipantau real-time." },
                        { icon: "💬", title: "Tagihan Otomatis", desc: "Pengingat dan tagihan sewa terkirim otomatis ke penyewa lewat WhatsApp."  },
                        { icon: "👥", title: "Kelola Bersama Tim", desc: "Ajak tim untuk ikut membantu mengelola kos." },
                      ].map((item, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                          <Box
                            border="1px solid #020202"
                            borderRadius={8}
                            p={2}
                            display="flex"
                            flexDirection="column"
                            height="100%"
                          >
                            <Typography variant="h5" gutterBottom>{item.icon}</Typography>
                            <Typography variant="subtitle1" gutterBottom>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {item.desc}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Box mt={3}>
                      <Typography variant="body2" color="textSecondary">
                        Ingin langsung mencoba sambil jalan, atau mau eksplorasi dulu? Yuk mulai 😊
                      </Typography>
                    </Box>
                  </Box>
                )}

                {step === 2 && (
                  <Box mb={2}>
                    <Typography variant="h6" gutterBottom>{t("Pengalaman Aplikasi")}</Typography>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      {t("Apakah Anda pernah menggunakan aplikasi manajemen sebelumnya?")}
                    </Typography>

                    <Grid container spacing={2} style={{ marginTop: 16 }}>
                      {experiencedOptions.map((option) => {
                        const selected = values.experiencedAppManagement === option.id;
                        return (
                          <Grid item xs={12} sm={6} key={option.id}>
                            <Box
                              onClick={() => {
                                setFieldValue("experiencedAppManagement", option.id);
                              }}
                              border={`2px solid ${
                                selected ? "#fbc02d" : "#020202"
                              }`}
                              borderRadius={8}
                              p={2}
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              style={{ cursor: "pointer" }}
                            >
                              <Box>
                                <Typography variant="subtitle1">{option.name}</Typography>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                                  {option.description}
                                </Typography>
                              </Box>
                              <Radio
                                checked={selected}
                                value={option.id}
                                onChange={() => setFieldValue("experiencedAppManagement", option.id)}
                                style={{
                                  color: selected ? "#fbc02d" : "#020202",
                                }}
                              />
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}

                {(step === 3 && experiencedAppManagementChoice === 'no') && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Yuk, lihat panduan singkat dulu 🚀
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      {t('Mau coba langsung sambil jalan, atau lihat panduan dulu?')}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {t('Kami siapkan video singkat agar Anda lebih cepat memahami cara menggunakan Superkos. Anda bisa menontonnya sebentar, atau langsung eksplor sesuai kenyamanan Anda.')}
                    </Typography>
                    
                    <div style={{ position: 'relative', paddingTop: '56.25%', marginBottom: 16, marginTop:20 }}>
                      <iframe
                        src="https://www.youtube.com/embed/6KoLMTfkPnw?si=VlvpPgGf7qy_JthB"
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Benefits Video"
                      />
                    </div>
                  </Box>
                )}

                {((experiencedAppManagementChoice === 'no' && step === 4) ||
                  (experiencedAppManagementChoice === 'yes' && step === 3)) && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {t("Darimana Anda mengetahui Superkos?")}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" gutterBottom>
                        {t("Jawaban Anda membantu kami memahami saluran terbaik agar bisa memberikan pengalaman yang lebih baik.")}
                      </Typography>
                      <SelectField
                        label={t("Lead Source*")}
                        name="leadSource"
                        values={availableSource}
                        className='reference-field'
                      />
                    </Box>
                  )
                }

                {/* Trial Step */}
                {((experiencedAppManagementChoice === 'no' && step === 5) ||
                  (experiencedAppManagementChoice === 'yes' && step === 4)) && (
                    <>
                      {/* Desktop Version - Unchanged */}
                      {!isMobile && (
                        <Box
                          p={4}
                          borderRadius={10}
                          bgcolor="#ffffff"
                          maxWidth={500}
                          mx="auto"
                          style={{
                            border: '2px solid #FCD34D',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            // marginTop: '-130px',
                            position: 'relative',
                            zIndex: 10
                          }}
                        >
                      {/* Gray Trust Badges Box */}
                      <Box
                        p={1}
                        borderRadius={4}
                        bgcolor="#f9fafb"
                        mb={2}
                      >
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Box textAlign="center">
                              <Typography style={{ color: '#F59E0B', fontSize: '14px', fontWeight: 700 }}>
                                FREE TRIAL
                              </Typography>
                              <Typography style={{ color: '#6b7280', fontSize: '10px', fontWeight: 500 }}>
                                30 Hari
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box textAlign="center" style={{ borderLeft: '1px solid #e5e7eb', borderRight: '1px solid #e5e7eb' }}>
                              <Typography style={{ color: '#F59E0B', fontSize: '14px', fontWeight: 700 }}>
                                OWNER KOS
                              </Typography>
                              <Typography style={{ color: '#6b7280', fontSize: '10px', fontWeight: 500 }}>
                                1000+
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box textAlign="center">
                              <Typography style={{ color: '#F59E0B', fontSize: '14px', fontWeight: 700 }}>
                                SUPPORT
                              </Typography>
                              <Typography style={{ color: '#6b7280', fontSize: '10px', fontWeight: 500 }}>
                                24/7
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>

                      {/* Yellow Badge */}
                      <Box textAlign="left" mb={2}>
                        <Box
                          display="inline-block"
                          px={1.5}
                          py={0.5}
                          borderRadius={8}
                          bgcolor="#FCD34D"
                        >
                          <Typography style={{ color: '#92400e', fontSize: '10px', fontWeight: 600 }}>
                            ⚡ Semuanya Siap!
                          </Typography>
                        </Box>
                      </Box>

                      {/* Main Heading */}
                      <Box textAlign="left" mb={1}>
                        <Typography variant="h6" style={{
                          fontWeight: 700,
                          fontSize: '22px',
                          color: '#111827',
                          lineHeight: 1.3
                        }}>
                          Mulai Kelola Kos Anda
                        </Typography>
                      </Box>

                      {/* Subtitle */}
                      <Box textAlign="left" mb={2}>
                        <Typography style={{
                          color: '#6b7280',
                          fontSize: '13px',
                          lineHeight: 1.4
                        }}>
                          Platform terintegrasi untuk mengelola properti dengan mudah dan efisien
                        </Typography>
                      </Box>

                      {/* Feature Cards - 2x2 Grid */}
                      <Grid container spacing={2} style={{ marginBottom: '25px' }}>
                        <Grid item xs={6}>
                          <Box
                            p={2.5}
                            borderRadius={6}
                            border="1px solid #e5e7eb"
                            bgcolor="#f9fafb"
                            style={{ height: '100%' }}
                          >
                            <Box display="flex" alignItems="center" mb={2}>
                              <Box
                                width={36}
                                height={36}
                                borderRadius={8}
                                bgcolor="#fbc02d"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                mr={1.5}
                                flexShrink={0}
                              >
                                <Home size={20} color="white" />
                              </Box>
                              <Typography style={{
                                fontSize: '14px',
                                color: '#111827',
                                fontWeight: 600,
                                lineHeight: 1.2
                              }}>
                                Kelola Kamar
                              </Typography>
                            </Box>
                            <Typography style={{
                              fontSize: '11px',
                              color: '#6b7280',
                              textAlign: 'left',
                              marginTop: '2px'
                            }}>
                              Dashboard terpadu untuk monitoring
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box
                            p={2.5}
                            borderRadius={6}
                            border="1px solid #e5e7eb"
                            bgcolor="#f9fafb"
                            style={{ height: '100%' }}
                          >
                            <Box display="flex" alignItems="center" mb={2}>
                              <Box
                                width={36}
                                height={36}
                                borderRadius={8}
                                bgcolor="#fbc02d"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                mr={1.5}
                                flexShrink={0}
                              >
                                <Users size={20} color="white" />
                              </Box>
                              <Typography style={{
                                fontSize: '14px',
                                color: '#111827',
                                fontWeight: 600,
                                lineHeight: 1.2
                              }}>
                                Manajemen Tenant
                              </Typography>
                            </Box>
                            <Typography style={{
                              fontSize: '11px',
                              color: '#6b7280',
                              textAlign: 'left',
                              marginTop: '2px'
                            }}>
                              Data lengkap tenant & komunikasi
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box
                            p={2.5}
                            borderRadius={6}
                            border="1px solid #e5e7eb"
                            bgcolor="#f9fafb"
                            style={{ height: '100%' }}
                          >
                            <Box display="flex" alignItems="center" mb={2}>
                              <Box
                                width={36}
                                height={36}
                                borderRadius={8}
                                bgcolor="#fbc02d"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                mr={1.5}
                                flexShrink={0}
                              >
                                <BarChart3 size={20} color="white" />
                              </Box>
                              <Typography style={{
                                fontSize: '14px',
                                color: '#111827',
                                fontWeight: 600,
                                lineHeight: 1.2
                              }}>
                                Laporan Keuangan
                              </Typography>
                            </Box>
                            <Typography style={{
                              fontSize: '11px',
                              color: '#6b7280',
                              textAlign: 'left',
                              marginTop: '2px'
                            }}>
                              Analytics real-time & laporan
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box
                            p={2.5}
                            borderRadius={6}
                            border="1px solid #e5e7eb"
                            bgcolor="#f9fafb"
                            style={{ height: '100%' }}
                          >
                            <Box display="flex" alignItems="center" mb={2}>
                              <Box
                                width={36}
                                height={36}
                                borderRadius={8}
                                bgcolor="#fbc02d"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                mr={1.5}
                                flexShrink={0}
                              >
                                <Bell size={20} color="white" />
                              </Box>
                              <Typography style={{
                                fontSize: '14px',
                                color: '#111827',
                                fontWeight: 600,
                                lineHeight: 1.2
                              }}>
                                Notifikasi Otomatis
                              </Typography>
                            </Box>
                            <Typography style={{
                              fontSize: '11px',
                              color: '#6b7280',
                              textAlign: 'left',
                              marginTop: '2px'
                            }}>
                              Reminder pembayaran & notif
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Yellow CTA Button */}
                      <Box style={{ marginTop: '15px', marginBottom: '8px' }}>
                        <Button
                          variant="contained"
                          size="large"
                          type="submit"
                          style={{
                            backgroundColor: '#F59E0B',
                            color: 'white',
                            fontWeight: 600,
                            padding: '10px 16px',
                            fontSize: '13px',
                            textTransform: 'none',
                            borderRadius: 6,
                            width: '100%',
                            height: '40px',
                            boxShadow: 'none',
                            border: 'none'
                          }}
                        >
                          ✨ Mulai Free Trial Sekarang
                        </Button>
                      </Box>

                      {/* Bottom Tagline */}
                      <Box textAlign="center">
                        <Typography style={{
                          fontSize: '11px',
                          color: '#6b7280',
                          fontStyle: 'italic'
                        }}>
                          Coba sekarang, bayar nanti
                        </Typography>
                      </Box>
                        </Box>
                      )}

                      {/* Mobile Version - Optimized */}
                      {isMobile && (
                        <Box
                          p={2}
                          borderRadius={8}
                          bgcolor="#ffffff"
                          maxWidth="98%"
                          mx="auto"
                          style={{
                            border: '2px solid #FCD34D',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            marginTop: '-60px',
                            position: 'relative',
                            zIndex: 10,
                            overflow: 'hidden',
                            wordBreak: 'break-word'
                          }}
                        >
                          {/* Yellow Badge */}
                          <Box textAlign="left" mb={1}>
                            <Box
                              display="inline-block"
                              px={1}
                              py={0.5}
                              borderRadius={6}
                              bgcolor="#FCD34D"
                            >
                              <Typography style={{ color: '#92400e', fontSize: '9px', fontWeight: 600 }}>
                                ⚡ Semuanya Siap!
                              </Typography>
                            </Box>
                          </Box>

                          {/* Main Heading */}
                          <Box textAlign="left" mb={1}>
                            <Typography variant="h6" style={{
                              fontWeight: 700,
                              fontSize: '16px',
                              color: '#111827',
                              lineHeight: 1.2,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word',
                              hyphens: 'auto'
                            }}>
                              Mulai Kelola Kos Anda
                            </Typography>
                          </Box>

                          {/* Subtitle */}
                          <Box textAlign="left" mb={2}>
                            <Typography style={{
                              color: '#6b7280',
                              fontSize: '12px',
                              lineHeight: 1.4,
                              wordWrap: 'break-word',
                              overflowWrap: 'break-word'
                            }}>
                              Platform terintegrasi untuk mengelola properti dengan mudah dan efisien
                            </Typography>
                          </Box>

                          {/* Feature Cards - 2x2 Grid */}
                          <Grid container spacing={1.5} style={{ marginBottom: '24px' }}>
                            <Grid item xs={6}>
                              <Box
                                p={2}
                                borderRadius={6}
                                border="1px solid #e5e7eb"
                                bgcolor="#f9fafb"
                                style={{
                                  height: '100%',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word'
                                }}
                              >
                                <Box display="flex" flexDirection="column" alignItems="center" mb={1.5}>
                                  <Box
                                    width={32}
                                    height={32}
                                    borderRadius={8}
                                    bgcolor="#fbc02d"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mb={1}
                                  >
                                    <Home size={18} color="white" />
                                  </Box>
                                  <Typography style={{
                                    fontSize: '11px',
                                    color: '#111827',
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    textAlign: 'center'
                                  }}>
                                    Kelola Kamar
                                  </Typography>
                                </Box>
                                <Typography style={{
                                  fontSize: '10px',
                                  color: '#6b7280',
                                  textAlign: 'center',
                                  // marginTop: '2px'
                                }}>
                                  Dashboard terpadu untuk monitoring
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box
                                p={2}
                                borderRadius={6}
                                border="1px solid #e5e7eb"
                                bgcolor="#f9fafb"
                                style={{
                                  height: '100%',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word'
                                }}
                              >
                                <Box display="flex" flexDirection="column" alignItems="center" mb={1.5}>
                                  <Box
                                    width={32}
                                    height={32}
                                    borderRadius={8}
                                    bgcolor="#fbc02d"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mb={1}
                                  >
                                    <Users size={18} color="white" />
                                  </Box>
                                  <Typography style={{
                                    fontSize: '11px',
                                    color: '#111827',
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    textAlign: 'center'
                                  }}>
                                    Manajemen Tenant
                                  </Typography>
                                </Box>
                                <Typography style={{
                                  fontSize: '10px',
                                  color: '#6b7280',
                                  textAlign: 'center',
                                  // marginTop: '2px'
                                }}>
                                  Data lengkap tenant & komunikasi
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box
                                p={2}
                                borderRadius={6}
                                border="1px solid #e5e7eb"
                                bgcolor="#f9fafb"
                                style={{
                                  height: '100%',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word'
                                }}
                              >
                                <Box display="flex" flexDirection="column" alignItems="center" mb={1.5}>
                                  <Box
                                    width={32}
                                    height={32}
                                    borderRadius={8}
                                    bgcolor="#fbc02d"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mb={1}
                                  >
                                    <BarChart3 size={18} color="white" />
                                  </Box>
                                  <Typography style={{
                                    fontSize: '11px',
                                    color: '#111827',
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    textAlign: 'center'
                                  }}>
                                    Laporan Keuangan
                                  </Typography>
                                </Box>
                                <Typography style={{
                                  fontSize: '10px',
                                  color: '#6b7280',
                                  textAlign: 'center',
                                  // marginTop: '2px'
                                }}>
                                  Analytics real-time & laporan
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box
                                p={2}
                                borderRadius={6}
                                border="1px solid #e5e7eb"
                                bgcolor="#f9fafb"
                                style={{
                                  height: '100%',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word'
                                }}
                              >
                                <Box display="flex" flexDirection="column" alignItems="center" mb={1.5}>
                                  <Box
                                    width={32}
                                    height={32}
                                    borderRadius={8}
                                    bgcolor="#fbc02d"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    mb={1}
                                  >
                                    <Bell size={18} color="white" />
                                  </Box>
                                  <Typography style={{
                                    fontSize: '11px',
                                    color: '#111827',
                                    fontWeight: 600,
                                    lineHeight: 1,
                                    textAlign: 'center'
                                  }}>
                                    Notifikasi Otomatis
                                  </Typography>
                                </Box>
                                <Typography style={{
                                  fontSize: '10px',
                                  color: '#6b7280',
                                  textAlign: 'center',
                                  // marginTop: '2px'
                                }}>
                                  Reminder pembayaran & notif
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Yellow CTA Button */}
                          <Box style={{ marginTop: '6px', marginBottom: '8px' }}>
                            <Button
                              variant="contained"
                              size="large"
                              type="submit"
                              style={{
                                backgroundColor: '#F59E0B',
                                color: 'white',
                                fontWeight: 600,
                                padding: '8px 14px',
                                fontSize: '11px',
                                textTransform: 'none',
                                borderRadius: 6,
                                width: '100%',
                                height: '36px',
                                boxShadow: 'none',
                                border: 'none'
                              }}
                            >
                              ✨ Mulai Free Trial Sekarang
                            </Button>
                          </Box>

                          {/* Bottom Tagline */}
                          <Box textAlign="center">
                            <Typography style={{
                              fontSize: '10px',
                              color: '#6b7280',
                              fontStyle: 'italic'
                            }}>
                              Coba sekarang, bayar nanti
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </>
                  )
                }
              </Box>

              {/* Navigation Buttons - Hide for trial step */}
              {!((experiencedAppManagementChoice === 'no' && step === 5) ||
                (experiencedAppManagementChoice === 'yes' && step === 4)) && (
                <div style={{ display: 'flex', justifyContent: step > 0 ? 'space-between' : 'flex-end', gap: 8 }}>
                  {step > 0 && (
                    <SubmitButton
                      type="button"
                      onClick={() => setStep(step - 1)}
                      style={{ backgroundColor: '#fff', color: '#0d0d0d' }}
                      label={t('Back')}
                    />
                  )}
                  <SubmitButton
                    size="large"
                    label={!isSubmitting ? (step === totalSteps - 1 ? t('Save') : t('Next')) : t('Saving')}
                    className='submit-button'
                    style={{ backgroundColor: '#0d0d0d', color: '#eee' }}
                  />
                </div>
              )}

              {/* Fixed Stepper Dots */}
              <div style={{
                position: 'fixed',
                bottom: 30,
                left: 0,
                right: 0,
                display: 'flex',
                justifyContent: 'center',
                padding: '10px 0',
                backgroundColor: 'transparent',
                gap: 12,
                zIndex: 1000,
              }}>
                {Array.from({ length: totalSteps }).map((_, idx) => (
                  <div
                    key={idx}
                    style={{
                      width: step === idx ? 24 : 12,
                      height: 12,
                      borderRadius: step === idx ? 6 : 12,
                      backgroundColor: step === idx ? '#0D0D0D' : '#c4c4c4',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                    }}
                    onClick={() => setStep(idx)}
                  />
                ))}
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
});


export default LandlordForm;
